import { useRouter } from 'next/router';
import { memo, MouseEventHandler, useState } from 'react';
import { BsArrowReturnLeft, BsArrowReturnRight, BsDot } from 'react-icons/bs';
import { PATH_NAME } from '../../configs/pathName';
import { ShowCommentState } from '../../constant/comment';
import {
  ICommentCardInfo,
  ICommentSideModal,
  ICommentUserInfo,
  ICommentWrapper,
  IGeneralComment,
} from '../../interfaces/components/comment.interface';
import { ICommentInteract } from '../../interfaces/hook.interface';
import CommentCard from '../cards/comment';
import { QuickLoginModalHOC } from '../modal/LoginModal2HOC';
import CommentForm from './CommentForm';
import CommentSkeleton from './CommentSkeleton';

export interface ICommentItem {
  comment: {
    userInfo: ICommentUserInfo;
    commentInfo: ICommentCardInfo;
  };
  isLastReply?: boolean;
  parentId?: string;
  badge?: React.ReactNode | React.ReactNode[];
  toast: (_content: string, _type: 'success' | 'error') => void;
  isPinned?: boolean;
  commentSectionId?: string;
}

const CommentItem = ({
  comment,
  handleDeleteGlobal,
  handleUpdateGlobal,
  handleVoteGlobal,
  handleAddGlobal,
  handleTwoWaysPinGlobal,
  badge,
  toast,
  openConfirm,
  isPinned,
  customDataMap,
  useCommentGeneralUtils,
  commentSectionId,
}: ICommentItem & ICommentSideModal & ICommentInteract & ICommentWrapper) => {
  const [showMoreComment, setShowMoreComment] = useState(
    ShowCommentState.Hidden
  );
  const [showReplyInput, setShowReplyInput] = useState(false);
  const router = useRouter();

  const {
    subCommentList,
    updateSubComment,
    addSubComment,
    voteSubComment,
    deleteSubComment,
    infinityQueryReturn: {
      data: fetchedSubCommentList,
      fetchNextPage,
      isFetching,
    },
  } = useCommentGeneralUtils.useReplyCommentHook(comment.commentInfo.id);

  const { commentListMeta: subCommentListMeta } =
    useCommentGeneralUtils.useMetaRoleHook(
      fetchedSubCommentList as unknown as IGeneralComment,
      subCommentList
    );

  const handleClickReply = () => {
    setShowReplyInput(true);
    subCommentList?.length > 0 && setShowMoreComment(ShowCommentState.Show);
  };

  const handlePostVote = async () => {
    try {
      await handleVoteGlobal(
        comment.commentInfo.id,
        !comment.commentInfo.isUpvoted
      );
    } catch (error) {
      toast(`Có lỗi xảy ra khi đánh giá bình luận`, 'error');
    }
  };

  return (
    <section className="mb-8">
      <CommentCard
        userInfo={comment.userInfo}
        commentInfo={comment.commentInfo}
        buttonList={
          <div className="flex items-center gap-1 pl-2 text-primary">
            <QuickLoginModalHOC
              handleClickWhenDisabledQuickLogin={handlePostVote}
              // this only use for comment in post
              loginSuccessCb={() => {
                router.push(
                  commentSectionId
                    ? {
                        pathname: PATH_NAME.HOME_FEED_POST_DETAIL,
                        query: {
                          pid: commentSectionId,
                        },
                      }
                    : PATH_NAME.LIBRARY
                );
              }}
            >
              <button
                className={`text-xs ${
                  comment.commentInfo.isUpvoted
                    ? 'text-[#4FA03B]'
                    : 'text-Primary/Main'
                }`}
              >
                Uy tín
              </button>
            </QuickLoginModalHOC>
            <BsDot className="text-gray-100" />
            <div>
              <button
                onClick={handleClickReply}
                className="text-xs text-Primary/Main"
              >
                Phản hồi
              </button>
            </div>
          </div>
        }
        handleDeleteGlobal={handleDeleteGlobal}
        handleUpdateGlobal={handleUpdateGlobal}
        handleVoteGlobal={handleVoteGlobal}
        handleAddGlobal={handleAddGlobal}
        handleTwoWaysPinGlobal={handleTwoWaysPinGlobal}
        badge={badge}
        toast={toast}
        openConfirm={openConfirm}
        isPinned={isPinned}
        commentSectionId={commentSectionId}
      />

      {showMoreComment !== ShowCommentState.Hidden && (
        <div className="w-full">
          {subCommentListMeta.totalCount > 0 &&
            subCommentList.map((subComment, key: number) => (
              <SubCommentCard
                comment={customDataMap(
                  subComment,
                  comment.userInfo.commentSectionRole
                )}
                key={subComment.id}
                isLastReply={key === subCommentList?.length - 1}
                parentId={comment.commentInfo.id}
                handleDeleteGlobal={deleteSubComment}
                handleUpdateGlobal={updateSubComment}
                handleVoteGlobal={voteSubComment}
                handleAddGlobal={addSubComment}
                toast={toast}
                openConfirm={openConfirm}
                commentSectionId={commentSectionId}
              />
            ))}

          {isFetching && <CommentSkeleton />}

          {!isFetching && (
            <>
              {subCommentListMeta.hasNextPage ? (
                <ShowMoreButton
                  handleClick={() => {
                    setShowMoreComment(ShowCommentState.ShowMore);
                    fetchNextPage();
                  }}
                />
              ) : (
                <CollapseSubCommentButton
                  handleClick={() =>
                    setShowMoreComment(ShowCommentState.Hidden)
                  }
                />
              )}
            </>
          )}
        </div>
      )}

      {showMoreComment === ShowCommentState.Hidden &&
        !isFetching &&
        subCommentListMeta.totalCount > 0 && (
          <ShowSubCommentsButton
            handleClick={handleClickReply}
            commentCounter={subCommentListMeta?.totalCount}
          />
        )}

      {showReplyInput && (
        <div className="mt-6">
          <CommentForm
            placeholder={'Nhập phản hồi tại đây'}
            parentId={comment.commentInfo.id}
            handleCancel={() => {
              setShowReplyInput(false);
            }}
            toast={toast}
            openConfirm={openConfirm}
            autoFocus
            handleAddGlobal={addSubComment}
            handleUpdateGlobal={updateSubComment}
            commentSectionId={commentSectionId}
          />
        </div>
      )}
    </section>
  );
};

const ShowSubCommentsButton = ({
  handleClick,
  commentCounter,
}: {
  handleClick: MouseEventHandler<HTMLButtonElement>;
  commentCounter: number | undefined;
}) => {
  if (!commentCounter) return null;
  return (
    <div className="mt-3 flex items-center gap-1 text-sm text-gray-500">
      <BsArrowReturnRight />
      <button onClick={handleClick}>{commentCounter} phản hồi</button>
    </div>
  );
};

const CollapseSubCommentButton = ({
  handleClick,
}: {
  handleClick: MouseEventHandler<HTMLButtonElement>;
}) => {
  return (
    <button
      onClick={handleClick}
      className="mb-2 mt-3 flex items-center gap-1 text-sm text-gray-500"
    >
      <BsArrowReturnLeft className="rotate-90" />
      <span>Thu gọn phản hồi</span>
    </button>
  );
};

const ShowMoreButton = ({
  handleClick,
}: {
  handleClick: MouseEventHandler<HTMLButtonElement>;
}) => {
  return (
    <button
      onClick={handleClick}
      className="mb-2 mt-3 flex items-center gap-1 text-sm text-gray-500"
    >
      <BsArrowReturnRight className="rotate-90" />
      <span>Xem thêm phản hồi</span>
    </button>
  );
};

const SubCommentCard = ({
  comment,
  isLastReply,
  parentId,
  ...prop
}: ICommentItem & ICommentSideModal & ICommentInteract) => {
  const router = useRouter();
  const handlePostVote = async () => {
    try {
      await prop.handleVoteGlobal(
        comment.commentInfo.id,
        !comment.commentInfo.isUpvoted
      );
    } catch (error) {
      prop.toast(`Có lỗi xảy ra khi đánh giá phản hồi`, 'error');
    }
  };

  return (
    <section className="relative my-4 flex w-full items-start gap-8 pl-5">
      {!isLastReply ? (
        <div className="root-comment-line absolute left-[20px] top-[20px] z-10"></div>
      ) : null}
      <div className="reply-line absolute left-[20px] top-0 z-10"></div>
      <section className="ml-9 w-full">
        <CommentCard
          userInfo={comment.userInfo}
          commentInfo={comment.commentInfo}
          buttonList={
            <div className="flex items-center gap-1 text-primary">
              <QuickLoginModalHOC
                handleClickWhenDisabledQuickLogin={handlePostVote}
                loginSuccessCb={() => {
                  router.push(
                    prop.commentSectionId
                      ? {
                          pathname: PATH_NAME.HOME_FEED_POST_DETAIL,
                          query: {
                            pid: prop.commentSectionId,
                          },
                        }
                      : PATH_NAME.HOME_FEED
                  );
                }}
              >
                <button
                  className={`text-xs ${
                    comment.commentInfo.isUpvoted
                      ? 'text-[#4FA03B]'
                      : 'text-Primary/Main'
                  }`}
                >
                  Uy tín
                </button>
              </QuickLoginModalHOC>
            </div>
          }
          parentId={parentId}
          {...prop}
        />
      </section>
    </section>
  );
};

export default memo(CommentItem);
